import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from "react";
import { Form, Row, Col } from "react-bootstrap";
import { useSelector } from "react-redux";
import { RootState } from "@reducers/rootReducer";
import {
  useUpdateProjectMutation,
  useCreateProjectLocationMutation,
  useUpdateProjectLocationMutation,
  useRemoveCrmIntegrationFromProjectMutation,
  useListClientsForAccountQuery,
} from "@generated";
import { TextField, DateField } from "@components/FormsV2";
import ControlledDropDownV2 from "@components/Forms/ControlledDropDownV2";
import DropdownApiJs from "@components/FormsV2/DropdownApiJs/DropdownApiJs";
import AutoCompleteText from "@components/Forms/AutoCompleteText/AutoCompleteText";
import AddressForm from "@components/Forms/AddressForm";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ProjectVariables from "../../ProjectVariables/ProjectVariables";
import useFlags from "@common/hooks/useFlags";
import useUnloadWarning from "@utils/customHooks/useUnloadWarning";
import {
  formatUnprocessibleResponse,
  handleAlert,
} from "@utils/helperFunctions";
import {
  getUpdatedProjectVariables,
  getUpdatedCrm,
  getUpdatedPaymentTerm,
  getUpdatedPresalesEngineer,
  getUpdatedRateTable,
  getUpdatedBusinessUnit,
  upsertClientId,
  upsertSalesExecutiveId,
  validateLocation,
  validateVariables,
} from "../projectUtils/projectHelperFunctions";
import useOauth from "@utils/customHooks/useOauth";
import IconTextButton from "@components/Buttons/IconTextButton/IconTextButton";
import ScopeStackModal from "@components/ScopeStackModal/ScopeStackModal";
import { ProjectVariable } from "../../Admin/ProfessionalServices/common/types/ProjectVariable";

interface OptionItem {
  value: string;
  label: string;
}

interface ClientItem {
  id: string;
  name: string;
  msaDate: string | null;
  rateTableId: string | null;
}

interface Props {
  projectId: string;
  accountId: string | undefined;
  accountSlug: string;
  fieldLabels: Record<string, string>;
  isCrm: boolean;
  isPsa: boolean;

  // Current project data
  projectData: any;
  includedClient: any;
  includedPaymentTerm: any;
  includedBusinessUnit: any;
  includedSalesExec: any;
  includedPresales: any;
  includedCrm: any;
  includedRateTable: any;

  // Options for dropdowns
  clients: ClientItem[];
  clientNames: string[];
  salesExecOptions: OptionItem[];
  presalesOptions: OptionItem[];
  paymentTermOptions: OptionItem[];
  rateTableOptions: OptionItem[];
  businessUnitOptions: OptionItem[];
  projectLocations: any[];

  // Callbacks
  refetchProject: () => void;

  // Permissions
  salesExecutiveCreatePermission?: boolean;
}

const ProjectOverviewV2Form = forwardRef(
  (
    {
      projectId,
      accountId,
      accountSlug,
      fieldLabels,
      isCrm,
      isPsa,
      projectData,
      includedClient,
      includedPaymentTerm,
      includedBusinessUnit,
      includedSalesExec,
      includedPresales,
      includedCrm,
      includedRateTable,
      clients: initialClients,
      clientNames: initialClientNames,
      salesExecOptions,
      presalesOptions,
      paymentTermOptions,
      rateTableOptions,
      businessUnitOptions,
      projectLocations: initialLocations,
      refetchProject,
      salesExecutiveCreatePermission = false,
    }: Props,
    ref
  ) => {
    const [authorizationCode] = useOauth();
    const apiHost = process.env.REACT_APP_SCOPESTACK_API_HOST;
    const projectStatus = projectData?.attributes?.status || "";
    const isReadOnly = projectStatus !== "building";

    // Feature Flags
    const {
      configCrmOpportunityRequired,
      configSalesExecutiveRequired,
      configPresalesEngineerRequired,
      configMsaDateHidden,
      configBusinessUnitRequired,
      configServiceLocationRequired,
    } = useFlags();

    // Mutations
    const [updateProject] = useUpdateProjectMutation();
    const [createProjectLocation] = useCreateProjectLocationMutation();
    const [updateProjectLocation] = useUpdateProjectLocationMutation();
    const [removeCrmIntegration] = useRemoveCrmIntegrationFromProjectMutation();

    // Project fields
    const [projectName, setProjectName] = useState<string>(
      projectData?.attributes?.["project-name"] || ""
    );
    const [tags, setTags] = useState<string>(
      projectData?.attributes?.["tag-list"]?.join(",") || ""
    );
    const [msaDate, setMsaDate] = useState<string | null>(
      projectData?.attributes?.["msa-date"] || null
    );

    // Client
    const [clientFilter, setClientFilter] = useState<string>("");
    const [clients, setClients] = useState<ClientItem[]>(initialClients);
    const [clientNames, setClientNames] =
      useState<string[]>(initialClientNames);
    const [clientName, setClientName] = useState<string>(
      includedClient?.attributes?.name || ""
    );
    const [newClientId, setNewClientId] = useState<string | null>(null);

    const { data: filteredClientsData } = useListClientsForAccountQuery(
      clientFilter.length > 0
        ? {
            slug: accountSlug,
            filter: { active: true, name: clientFilter },
            include: ["rate-table"] as any,
            page: { size: 100 },
          }
        : {
            slug: accountSlug,
            filter: { active: true },
            include: ["rate-table"] as any,
            page: { size: 100 },
          }
    );

    useEffect(() => {
      if (filteredClientsData?.data) {
        const updated = filteredClientsData.data.map((client: any) => ({
          id: client.id?.toString() || "",
          name: client.attributes?.name || "",
          msaDate: client.attributes?.["msa-date"] || null,
          rateTableId:
            client.relationships?.["rate-table"]?.data?.id?.toString() || null,
        }));
        setClients(updated);
        setClientNames(updated.map((c) => c.name));
      }
    }, [filteredClientsData]);

    // CRM
    const [crmState, setCrmState] = useState<any[]>([]);
    const [crmId, setCrmId] = useState<string>(includedCrm?.id || "");
    const [currentCrmOpportunity, setCurrentCrmOpportunity] = useState<string>(
      includedCrm?.attributes?.["display-name"] ||
        includedCrm?.attributes?.name ||
        ""
    );
    const [selectedCrmOption, setSelectedCrmOption] = useState<{
      value: string | number;
      label: string;
    } | null>(null);
    const [crmOppInvalid, setCrmOppInvalid] = useState(false);
    const [showRemoveCrmModal, setShowRemoveCrmModal] = useState(false);

    // Sales Executive
    const [currentSalesExec, setCurrentSalesExec] = useState<OptionItem>({
      value: includedSalesExec?.id?.toString() || "",
      label: includedSalesExec?.attributes?.name || "",
    });
    const [newSalesExecId, setNewSalesExecId] = useState<string | null>(
      includedSalesExec?.id?.toString() || null
    );
    const [salesExecInvalid, setSalesExecInvalid] = useState(false);

    // Presales Engineer
    const [currentPresalesEngineer, setCurrentPresalesEngineer] =
      useState<string>(includedPresales?.attributes?.name || "");
    const [newPresalesEngineerId, setNewPresalesEngineerId] = useState<
      string | null
    >(includedPresales?.id?.toString() || null);

    // Payment Term
    const [newPaymentTermId, setNewPaymentTermId] = useState<string | null>(
      includedPaymentTerm?.id?.toString() || null
    );

    // Rate Table — track by ID so the save payload always has the correct value
    const [newRateTableId, setNewRateTableId] = useState<string | null>(
      includedRateTable?.id?.toString() || null
    );

    // Business Unit
    const [currentBusinessUnit, setCurrentBusinessUnit] = useState<string>(
      includedBusinessUnit?.attributes?.name || ""
    );
    const [newBusinessUnitId, setNewBusinessUnitId] = useState<string | null>(
      includedBusinessUnit?.id?.toString() || null
    );

    // Location
    const [projectLocations] = useState(initialLocations);
    const [locationId, setLocationId] = useState<string | null>(
      initialLocations.length > 0 ? initialLocations[0].id : null
    );
    const [addressChanged, setAddressChanged] = useState(false);
    const [customerSite, setCustomerSite] = useState<string>(
      initialLocations[0]?.attributes?.name || ""
    );
    const [remoteSite, setRemoteSite] = useState<boolean>(
      initialLocations[0]?.attributes?.remote || false
    );
    const [address, setAddress] = useState<string>(
      initialLocations[0]?.attributes?.street || ""
    );
    const [street2, setStreet2] = useState<string>(
      initialLocations[0]?.attributes?.street2 || ""
    );
    const [city, setCity] = useState<string>(
      initialLocations[0]?.attributes?.city || ""
    );
    const [state, setState] = useState<string>(
      initialLocations[0]?.attributes?.state || ""
    );
    const [zip, setZip] = useState<string>(
      initialLocations[0]?.attributes?.["postal-code"] || ""
    );
    const [country, setCountry] = useState<string>(
      initialLocations[0]?.attributes?.country || ""
    );
    const [countryId, setCountryId] = useState<string>("");

    // Normalize a project variable object so that ProjectVariables can
    // read its keys regardless of whether RTK Query camelized them.
    const normalizeVariable = (pv: any) => {
      const options =
        pv["select_options"] || pv.selectOptions || pv["select-options"] || [];
      const variableType =
        pv["variable_type"] || pv["variable-type"] || pv.variableType || "";
      const normalized = {
        ...pv,
        select_options: options,
        variable_type: variableType,
        "variable-type": variableType,
      };
      if (options.length > 0) {
        const foundOption = options.find((opt: any) => opt.default);
        if (foundOption && (!pv.value || pv.value === "")) {
          return { ...normalized, value: foundOption.value };
        }
      }
      return normalized;
    };

    // Project Variables — initialize synchronously so ProjectVariables
    // receives data on first render (its useEffect doesn't watch pvArrPassed)
    const initProjectVars = () => {
      const rawVars =
        projectData?.attributes?.projectVariables ||
        projectData?.attributes?.["project-variables"] ||
        [];
      return rawVars.map(normalizeVariable);
    };
    const [requiredProjectVariables, setRequiredProjectVariables] = useState<
      ProjectVariable[]
    >(() => initProjectVars().filter((v: any) => v.required === true));
    const [unrequiredProjectVariables, setUnrequiredProjectVariables] =
      useState<ProjectVariable[]>(() =>
        initProjectVars().filter((v: any) => v.required === false)
      );

    const initLocationVars = () => {
      if (initialLocations.length === 0) return [];
      const rawLocVars =
        initialLocations[0]?.attributes?.projectVariables ||
        initialLocations[0]?.attributes?.["project-variables"] ||
        [];
      return rawLocVars.map(normalizeVariable);
    };
    const [requiredLocationVariables, setRequiredLocationVariables] = useState<
      ProjectVariable[]
    >(() => initLocationVars().filter((v: any) => v.required === true));
    const [unrequiredLocationVariables, setUnrequiredLocationVariables] =
      useState<ProjectVariable[]>(() =>
        initLocationVars().filter((v: any) => v.required === false)
      );

    // Alerts
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [successText, setSuccessText] = useState("Project changes saved");
    const [showFailAlert, setShowFailAlert] = useState(false);
    const [submitFail, setSubmitFail] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [showLocationAlert, setShowLocationAlert] = useState(false);
    const [locationErrors, setLocationErrors] = useState<string[]>([]);
    const [showMinCharAlert, setShowMinCharAlert] = useState(false);
    const [showMaxCharAlert, setShowMaxCharAlert] = useState(false);
    const [minValue, setMinValue] = useState<number | null>(null);
    const [maxValue, setMaxValue] = useState<number | null>(null);

    // Form dirty tracking
    const [isFormDirty, setFormDirty] = useState(false);
    useUnloadWarning(isFormDirty);

    // Expose saveProject method to parent via ref
    useImperativeHandle(ref, () => ({
      saveProject: () => {
        handleSubmit(new Event("submit") as any);
      },
    }));

    // CRM change handler
    const handleCrmChange = (e: any) => {
      setCrmOppInvalid(false);

      if (!e.value || e.value === -1) {
        setCrmId("");
        setCurrentCrmOpportunity("");
        setSelectedCrmOption(null);
      } else {
        setCrmId(e.value);
        setSelectedCrmOption(e);
        for (let i = 0; i < crmState.length; i++) {
          if (crmState[i].id === e.value) {
            const crmAttr = crmState[i].attributes;
            setProjectName(crmAttr.name);
            setClientName(crmAttr["account-name"]);

            if (projectLocations.length <= 1) {
              const locationName = crmAttr["location-name"];
              setCustomerSite(locationName || crmAttr["account-name"]);
              setAddress(crmAttr.street || "");
              if (crmAttr.street2) setStreet2(crmAttr.street2);
              setState(crmAttr.state || "");
              setCity(crmAttr.city || "");
              setZip(crmAttr["postal-code"] || "");
              if (crmAttr.country) setCountry(crmAttr.country);
              setAddressChanged(true);
            }

            if (crmAttr["owner-name"]) {
              upsertSalesExecutiveId(
                crmAttr["owner-name"],
                accountSlug,
                authorizationCode,
                accountId
              ).then((salesExec: any) => {
                setNewSalesExecId(salesExec.id);
                setCurrentSalesExec({
                  label: crmAttr["owner-name"],
                  value: salesExec.id,
                });
              });
            }
          }
        }
      }
    };

    // Render CRM field
    const renderCrmOpportunity = () => {
      if (currentCrmOpportunity) {
        const crmLabel = `${
          fieldLabels["crm_opportunity"] || "CRM Opportunity"
        } ${configCrmOpportunityRequired ? "*" : ""}`;
        return (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <span className="form-label" style={{ marginBottom: 0 }}>
                {crmLabel}
              </span>
              {!isReadOnly && (
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowRemoveCrmModal(true);
                  }}
                  style={{
                    color: "#f15825",
                    fontSize: "0.875rem",
                    whiteSpace: "nowrap",
                  }}
                >
                  <i
                    className="fa fa-chain-broken"
                    style={{ marginRight: "4px" }}
                  />
                  Remove
                </a>
              )}
            </div>
            <TextField
              label={null}
              id="crmOpportunity"
              value={currentCrmOpportunity}
              onChange={() => {}}
              readOnly
              disabled
            />
          </>
        );
      }

      return (
        <DropdownApiJs
          value={selectedCrmOption}
          setValue={setSelectedCrmOption}
          placeholder={`Select ${
            fieldLabels["crm_opportunity"] || "CRM Opportunity"
          }`}
          defaultValue={{ value: -1, label: "-- None --" }}
          onChange={handleCrmChange}
          onInputChange={null}
          required={configCrmOpportunityRequired}
          label={`${fieldLabels["crm_opportunity"] || "CRM Opportunity"} ${
            configCrmOpportunityRequired ? "*" : ""
          }`}
          className=""
          isInvalid={crmOppInvalid}
          onFocus={null}
          url={`${apiHost}/${accountSlug}/v1/crm-opportunities`}
          isDisabled={isReadOnly}
          filter="display-name"
          blankOption={{ value: -1, label: "--None--" }}
          canCreate={false}
          setDataState={setCrmState}
        />
      );
    };

    // Render location fields
    const renderLocation = () => {
      if (projectLocations.length >= 2) return null;

      return (
        <>
          <AddressForm
            label={
              configServiceLocationRequired
                ? (fieldLabels.location || "Service Location") + " *"
                : fieldLabels.location || "Service Location"
            }
            required={configServiceLocationRequired}
            onSiteChange={(e: any) => {
              setCustomerSite(e.target.value);
              setAddressChanged(true);
            }}
            siteName={customerSite}
            onAddressChange={(e: any) => {
              setAddress(e.target.value);
              setAddressChanged(true);
            }}
            remoteLocation={remoteSite}
            onRemoteChanged={(e: any) => {
              setRemoteSite(e.target.checked);
              setAddressChanged(true);
            }}
            addressText={address}
            onCityChange={(e: any) => {
              setCity(e.target.value);
              setAddressChanged(true);
            }}
            cityText={city}
            onStateChange={(e: any) => {
              setState(e.target.value);
              setAddressChanged(true);
            }}
            stateText={state}
            onZipChange={(e: any) => {
              setZip(e.target.value);
              setAddressChanged(true);
            }}
            zipText={zip}
            placeholder="Customer Site"
            street2Text={street2}
            onStreet2Change={(e: any) => {
              setStreet2(e.target.value);
              setAddressChanged(true);
            }}
            country={country}
            onCountryChange={(e: any) => {
              setCountry(e);
              setCountryId(e.id);
              setAddressChanged(true);
            }}
          />
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              flexDirection: "row",
              justifyContent: "space-between",
            }}
          >
            <ProjectVariables
              overridePadding
              arr={requiredLocationVariables}
              account_slug={accountSlug}
              project_id={projectId}
              setState={setRequiredLocationVariables}
              setVariableChanged={setAddressChanged}
            />
            <ProjectVariables
              overridePadding
              arr={unrequiredLocationVariables}
              account_slug={accountSlug}
              project_id={projectId}
              setState={setUnrequiredLocationVariables}
              setVariableChanged={setAddressChanged}
            />
          </div>
        </>
      );
    };

    // Render location buttons
    const renderLocationButtons = () => {
      if (isReadOnly) return null;
      const buttons: JSX.Element[] = [];

      if (projectLocations.length === 1) {
        buttons.push(
          <Col key="addLocation">
            <IconTextButton
              url={`/projects/${projectId}/project_locations/new`}
              dataMethod={null}
              className="addLocation"
              iconClass="fa fa-plus"
              buttonText="Add Service Location"
              uniqueStyles={null}
            />
          </Col>
        );
      }
      if (projectLocations.length >= 1) {
        buttons.push(
          <Col key="manageLocations">
            <IconTextButton
              url={`/projects/${projectId}/project_locations`}
              dataMethod={null}
              className="addLocation"
              iconClass="fa fa-edit"
              buttonText="Manage Service Locations"
              uniqueStyles={null}
            />
          </Col>
        );
      }
      return <>{buttons}</>;
    };

    // Handle form submission
    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();

      // CRM validation
      if (!crmId && configCrmOpportunityRequired) {
        window.scrollTo(0, 0);
        setCrmOppInvalid(true);
        return;
      }

      // Sales exec validation
      if (configSalesExecutiveRequired && currentSalesExec.label === "") {
        setSalesExecInvalid(true);
        return;
      }

      setFormDirty(false);

      // Variable validation
      const allProjectVariables = requiredProjectVariables
        .concat(unrequiredProjectVariables)
        .concat(requiredLocationVariables)
        .concat(unrequiredLocationVariables);

      const variablesValid = validateVariables(
        allProjectVariables,
        setMinValue,
        setMaxValue,
        setShowMinCharAlert,
        setShowMaxCharAlert
      );
      if (!variablesValid) return;

      // Location validation
      const locationValid = validateLocation(
        addressChanged,
        customerSite,
        address,
        city,
        state,
        zip,
        remoteSite,
        setLocationErrors
      );
      if (!locationValid) {
        setShowLocationAlert(true);
        return;
      }

      // Build promises for upserts
      const projectPromises: Promise<any>[] = [];
      if (clientName !== "") {
        projectPromises.push(
          upsertClientId(clientName, accountSlug, authorizationCode, accountId)
        );
      }
      if (currentSalesExec.label?.trim()) {
        projectPromises.push(
          upsertSalesExecutiveId(
            currentSalesExec.label,
            accountSlug,
            authorizationCode,
            accountId
          )
        );
      }

      // Handle location upsert
      if (addressChanged) {
        const allLocationVariables = requiredLocationVariables.concat(
          unrequiredLocationVariables
        );
        const locationVariables = allLocationVariables.map((v) => ({
          name: v.name,
          value: v.value,
        }));

        const locationData = {
          data: {
            type: "project-locations" as const,
            attributes: {
              name: customerSite,
              street: address,
              street2: street2,
              city: city,
              state: state,
              "postal-code": zip,
              country: countryId || country,
              remote: remoteSite,
              "project-variables": locationVariables,
            },
            relationships: {
              project: {
                data: { type: "projects" as const, id: Number(projectId) },
              },
            },
          },
        };

        if (locationId) {
          projectPromises.push(
            updateProjectLocation({
              slug: accountSlug,
              id: Number(locationId),
              body: {
                data: { ...locationData.data, id: Number(locationId) } as any,
              },
            })
              .unwrap()
              .then((res: any) => {
                if (res?.data?.id) setLocationId(res.data.id);
              })
          );
        } else {
          projectPromises.push(
            createProjectLocation({
              slug: accountSlug,
              body: locationData as any,
            })
              .unwrap()
              .then((res: any) => {
                if (res?.data?.id) setLocationId(res.data.id);
              })
          );
        }
      }

      // Build project update payload
      let updatedProjectData: any = {
        data: {
          type: "projects",
          id: Number(projectId),
          attributes: {
            "project-name": projectName,
            "project-variables":
              getUpdatedProjectVariables(allProjectVariables),
            "tag-list": tags.trim(),
            "msa-date": msaDate,
          },
          relationships: {
            account: { data: { type: "accounts", id: Number(accountId) } },
            "crm-opportunity": getUpdatedCrm(crmId),
            "payment-term": getUpdatedPaymentTerm(newPaymentTermId),
            "presales-engineer": getUpdatedPresalesEngineer(
              newPresalesEngineerId
            ),
            "business-unit": getUpdatedBusinessUnit(newBusinessUnitId),
            "rate-table": getUpdatedRateTable(newRateTableId),
          },
        },
      };

      try {
        if (projectPromises.length > 0) {
          const values = await Promise.all(projectPromises);
          values.forEach((res: any) => {
            if (res?.type === "clients") {
              updatedProjectData.data.relationships["client"] = { data: res };
            }
            if (res?.type === "sales-executives") {
              if (res.id) {
                updatedProjectData.data.relationships["sales-executive"] = {
                  data: res,
                };
              } else {
                setCurrentSalesExec({ label: "", value: "" });
                delete updatedProjectData.data.relationships["sales-executive"];
              }
            }
          });

          if (
            updatedProjectData.data.relationships["client"]?.data?.id?.error
          ) {
            setErrorMessage(
              updatedProjectData.data.relationships["client"].data.id.error
            );
            setShowFailAlert(true);
            setSubmitFail(true);
            return;
          }
        }

        await updateProject({
          slug: accountSlug,
          id: Number(projectId),
          body: updatedProjectData,
        }).unwrap();

        // If a CRM was selected, update local state so the UI switches
        // to the disabled text field display
        if (crmId && selectedCrmOption?.label) {
          setCurrentCrmOpportunity(selectedCrmOption.label);
        }

        setSuccessText("Project changes saved");
        setShowSuccessAlert(true);
        setTimeout(() => setShowSuccessAlert(false), 3000);
        refetchProject();
      } catch (err: any) {
        // RTK Query .unwrap() rejects with { status, data } directly,
        // but formatUnprocessibleResponse expects axios shape { response: { status, data } }.
        // Wrap the error so the formatter can parse it.
        const axiosShapedErr = err?.response
          ? err
          : { response: { status: err?.status, data: err?.data } };
        try {
          setErrorMessage(
            formatUnprocessibleResponse(axiosShapedErr, "Project")
          );
        } catch {
          setErrorMessage(
            "Something went wrong! Some of your changes could not be saved at this time."
          );
        }
        setShowFailAlert(true);
        setSubmitFail(true);
      }
    };

    return (
      <>
        {submitFail
          ? handleAlert(
              showFailAlert,
              errorMessage,
              setShowFailAlert,
              "danger",
              ToastAlert
            )
          : handleAlert(
              showSuccessAlert,
              successText,
              setShowSuccessAlert,
              "success",
              ToastAlert
            )}
        {showLocationAlert && locationErrors.length > 0 && (
          <ToastAlert
            variant="warning"
            onClose={() => setShowLocationAlert(false)}
            text={locationErrors}
          />
        )}
        {showMinCharAlert && (
          <ToastAlert
            variant="danger"
            onClose={() => setShowMinCharAlert(false)}
            text={`Required text is too short (minimum is ${minValue} characters)`}
          />
        )}
        {showMaxCharAlert && (
          <ToastAlert
            variant="danger"
            onClose={() => setShowMaxCharAlert(false)}
            text={`Required text is too long (maximum is ${maxValue} characters)`}
          />
        )}

        <Form
          style={{ marginLeft: "16px" }}
          onChange={() => setFormDirty(true)}
          onSubmit={handleSubmit}
        >
          {/* CRM Section */}
          {isCrm && <section>{renderCrmOpportunity()}</section>}

          <ScopeStackModal
            show={showRemoveCrmModal}
            modalTitle="Remove CRM Opportunity"
            modalBody="Do you want to remove this CRM Opportunity from the project?"
            button1Text="Cancel"
            button2Text="Remove"
            handleClose={() => setShowRemoveCrmModal(false)}
            handleButton1Click={() => setShowRemoveCrmModal(false)}
            handleButton2Click={() => {
              setShowRemoveCrmModal(false);
              removeCrmIntegration({
                slug: accountSlug,
                id: Number(projectId),
              })
                .unwrap()
                .then(() => {
                  setCurrentCrmOpportunity("");
                  setCrmId("");
                  setSelectedCrmOption(null);
                  setSuccessText("CRM Opportunity removed");
                  setShowSuccessAlert(true);
                  setTimeout(() => setShowSuccessAlert(false), 3000);
                  refetchProject();
                })
                .catch((err: any) => {
                  const axiosShapedErr = err?.response
                    ? err
                    : { response: { status: err?.status, data: err?.data } };
                  try {
                    setErrorMessage(
                      formatUnprocessibleResponse(
                        axiosShapedErr,
                        "CRM Integration"
                      )
                    );
                  } catch {
                    setErrorMessage(
                      "Something went wrong removing the CRM Opportunity."
                    );
                  }
                  setShowFailAlert(true);
                  setSubmitFail(true);
                });
            }}
            deleteModal
          />

          {isCrm && <hr style={{ marginRight: "10px" }} />}

          {/* Main Form Fields */}
          <section>
            <Row>
              {/* Left Column */}
              <Col xs={6}>
                <TextField
                  required
                  id="projectName"
                  onChange={(e) => setProjectName(e.target.value)}
                  value={projectName}
                  label="Project Name *"
                  disabled={isReadOnly}
                />

                <Row
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    rowGap: "16px",
                  }}
                >
                  <Col sm={6}>
                    <DropdownApiJs
                      label={
                        configSalesExecutiveRequired
                          ? (fieldLabels["sales_executive"] ||
                              "Sales Executive") + " *"
                          : fieldLabels["sales_executive"] || "Sales Executive"
                      }
                      value={currentSalesExec}
                      setValue={null}
                      defaultValue={null}
                      placeholder=""
                      required={configSalesExecutiveRequired}
                      isInvalid={
                        salesExecInvalid && configSalesExecutiveRequired
                      }
                      onChange={(e: any) => {
                        setSalesExecInvalid(false);
                        setCurrentSalesExec(e);
                        setNewSalesExecId(e.value);
                      }}
                      onInputChange={null}
                      onFocus={null}
                      className=""
                      url={`${apiHost}/${accountSlug}/v1/sales-executives`}
                      isDisabled={isReadOnly}
                      filter="name"
                      blankOption={null}
                      canCreate={salesExecutiveCreatePermission}
                      setDataState={null}
                    />
                  </Col>
                  <Col sm={6}>
                    <ControlledDropDownV2
                      label={
                        configPresalesEngineerRequired
                          ? (fieldLabels["presales_engineer"] ||
                              "Pre-Sales Engineer") + " *"
                          : fieldLabels["presales_engineer"] ||
                            "Pre-Sales Engineer"
                      }
                      value={currentPresalesEngineer}
                      required={configPresalesEngineerRequired}
                      readOnly={isReadOnly}
                      disabled={isReadOnly}
                      onChange={(e: any) => {
                        const selected = e.target.options.selectedIndex;
                        setCurrentPresalesEngineer(e.target.value);
                        setNewPresalesEngineerId(
                          e.target.options[selected].accessKey
                        );
                      }}
                      options={[
                        <option key="" value="" accessKey=""></option>,
                        ...presalesOptions.map((pe) => (
                          <option key={pe.value} accessKey={pe.value}>
                            {pe.label}
                          </option>
                        )),
                      ]}
                    />
                  </Col>
                  <Col sm={6}>
                    <ControlledDropDownV2
                      label="Payment Term *"
                      value={newPaymentTermId || ""}
                      required
                      readOnly={isReadOnly}
                      disabled={isReadOnly}
                      onChange={(e: any) => {
                        setNewPaymentTermId(e.target.value);
                      }}
                      options={paymentTermOptions.map((pt) => (
                        <option key={pt.value} value={pt.value}>
                          {pt.label}
                        </option>
                      ))}
                    />
                  </Col>
                  {rateTableOptions.length > 1 && (
                    <Col sm={6}>
                      <ControlledDropDownV2
                        label="Rate Table *"
                        value={newRateTableId || ""}
                        required={false}
                        readOnly={false}
                        disabled={false}
                        onChange={(e: any) => {
                          setNewRateTableId(e.target.value);
                        }}
                        options={rateTableOptions.map((rt) => (
                          <option key={rt.value} value={rt.value}>
                            {rt.label}
                          </option>
                        ))}
                      />
                    </Col>
                  )}
                  {businessUnitOptions.length > 0 && (
                    <Col sm={6}>
                      <ControlledDropDownV2
                        label={
                          configBusinessUnitRequired
                            ? (fieldLabels["business_unit"] ||
                                "Business Unit") + " *"
                            : fieldLabels["business_unit"] || "Business Unit"
                        }
                        value={currentBusinessUnit}
                        required={configBusinessUnitRequired}
                        readOnly={false}
                        disabled={false}
                        onChange={(e: any) => {
                          setCurrentBusinessUnit(e.target.value);
                          const selected = e.target.options.selectedIndex;
                          setNewBusinessUnitId(
                            e.target.options[selected].accessKey
                          );
                        }}
                        options={[
                          <option key="" value="" accessKey=""></option>,
                          ...businessUnitOptions.map((bu) => (
                            <option key={bu.value} accessKey={bu.value}>
                              {bu.label}
                            </option>
                          )),
                        ]}
                      />
                    </Col>
                  )}
                  {!configMsaDateHidden && (
                    <Col sm={6}>
                      <DateField
                        label="MSA Date"
                        value={msaDate}
                        onChange={(e) => setMsaDate(e.target.value)}
                        disabled={isReadOnly}
                      />
                    </Col>
                  )}
                  <ProjectVariables
                    arr={requiredProjectVariables}
                    account_slug={accountSlug}
                    project_id={projectId}
                    setState={setRequiredProjectVariables}
                    setVariableChanged={null}
                  />
                  <ProjectVariables
                    arr={unrequiredProjectVariables}
                    account_slug={accountSlug}
                    project_id={projectId}
                    setState={setUnrequiredProjectVariables}
                    setVariableChanged={null}
                  />
                </Row>
              </Col>

              {/* Right Column */}
              <Col xs={6}>
                {currentCrmOpportunity || isReadOnly ? (
                  <TextField
                    id="clientName"
                    label={(fieldLabels.client || "Client") + " *"}
                    value={clientName}
                    onChange={() => {}}
                    readOnly
                    disabled
                  />
                ) : (
                  <AutoCompleteText
                    required
                    id="clientName"
                    label={(fieldLabels.client || "Client") + " *"}
                    placeholder=""
                    suggestionList={clientNames}
                    value={clientName}
                    monitorValue={(value: string) => {
                      setClientName(value);
                      setClientFilter(value);
                      for (let i = 0; i < clients.length; i++) {
                        if (clients[i].name === value) {
                          setMsaDate(clients[i].msaDate);
                          setNewClientId(clients[i].id);
                          if (clients[i].rateTableId) {
                            setNewRateTableId(clients[i].rateTableId);
                          }
                        }
                      }
                    }}
                    setTextChanged={() => {}}
                    crmId={crmId}
                  />
                )}
                {renderLocation()}
                <Row>{renderLocationButtons()}</Row>
                <TextField
                  id="tag-list"
                  value={tags}
                  onChange={(e) => {
                    setTags(e.target.value);
                    setShowFailAlert(false);
                    setSubmitFail(false);
                  }}
                  label="Tags"
                  disabled={isReadOnly}
                />
                <Form.Text muted id="tag-list-info">
                  Comma-separated list of tags for the project
                </Form.Text>
              </Col>
            </Row>
          </section>
        </Form>
      </>
    );
  }
);

export default ProjectOverviewV2Form;
